import * as sharedApi from '../../utils/api/sharedApi';

import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { deleteDocuments, getImages, updateImages as updateImagesApi, uploadImages as uploadImagesApi } from '../../utils/api/documentsApi';
import { importRevideaImagesApi } from '../../utils/api/revideaApi';
import { SagaIterator } from 'redux-saga';

import { deleteImages,
  deleteImagesError,
  deleteImagesSuccess,
  fetchImages,
  fetchImagesError,
  fetchImagesSuccess,
  getImageSrc,
  getImageSrcFail,
  getImageSrcSuccess,
  setImagesIsDirty,
  updateImages,
  updateImagesFail,
  updateImagesSuccess,
  uploadImages,
  uploadImagesError,
  uploadImagesSuccess,
  importRevideaImages,
  importRevideaImagesError} from './actions';
import { setToastMessage, toggleIsUIDisabled } from '../../shared/actions';

import { DetailsImagesActionTypes } from './types';
import { ToastMessageType } from '../../shared/model';
import { CaseDocumentsRequest } from '../shared/model';
import { getI18n } from '../../../store/utils/i18n/strings.manager';

function* handleFetchImages(action: ReturnType<typeof fetchImages>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const res = yield call(getImages, action.payload.instructionId);

    if (!(res.status === 200)) {
      yield put(fetchImagesError(JSON.stringify(res.response)));
    } else {
      yield put(fetchImagesSuccess(res.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchImagesError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchImagesError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleUploadImages(action: ReturnType<typeof uploadImages>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));

    // Sanitize filenames in all documents
    const sanitizedDocuments = action.payload.request.documents.map(doc => ({
      ...doc,
      filename: sanitizeFilename(doc.filename),
    }));

    const sanitizedRequest: CaseDocumentsRequest = {
      ...action.payload.request,
      documents: sanitizedDocuments,
    };

    const res = yield call(uploadImagesApi, action.payload.instructionId, sanitizedRequest);

    if (!(res.status === 200)) {
      yield put(uploadImagesError(JSON.stringify(res.response)));
    } else {
      yield put(uploadImagesSuccess(res.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(uploadImagesError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(uploadImagesError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleDeleteImages(action: ReturnType<typeof deleteImages>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const res = yield call(deleteDocuments, action.payload.request);

    if (!(res.status === 200)) {
      yield put(deleteImagesError(JSON.stringify(res.response)));
    } else {
      yield put(deleteImagesSuccess(action.payload.request.map(x => x.id)));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(deleteImagesError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(deleteImagesError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleUpdateImages(action: ReturnType<typeof updateImages>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const res = yield call(updateImagesApi, action.payload.request);

    if (!(res.status === 200)) {
      yield put(updateImagesFail(JSON.stringify(res.response)));
    } else {
      yield put(updateImagesSuccess(res.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(updateImagesFail(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(updateImagesFail('handleUpdateImages fail'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleUpdateImagesSuccess(action: ReturnType<typeof updateImagesSuccess>): SagaIterator {
  const localization = getI18n();
  yield all([
    put(setToastMessage({
      type: ToastMessageType.SUCCESS,
      messages: [localization.formatString(
        'images.updated', {
          imageCount: `${action.payload.images.length}`
        }).toString()  ]
    })),
    put(setImagesIsDirty(action.payload.images.map(x => ({ id: x.id, isDirty: false }))))
  ]);
}

function* handleGetImageSrc(action: ReturnType<typeof getImageSrc>): SagaIterator {
  const result = yield call(sharedApi.getDownloadLink, action.payload.amazonKey, action.payload.bucketName);

  if (!result) {
    yield put(getImageSrcFail(JSON.stringify(result.response)));
  } else {
    yield put(getImageSrcSuccess(action.payload.id, result.url));
  }
}


function* handleImportRevideaImages(action: ReturnType<typeof importRevideaImages>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const res = yield call(importRevideaImagesApi, action.payload.instructionId, action.payload.organisationId);

    if (!(res.status === 200)) {
      yield put(importRevideaImagesError(JSON.stringify(res.response)));
    } else {
      yield put(fetchImages(action.payload.instructionId));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(importRevideaImagesError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(importRevideaImagesError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function sanitizeFilename(filename: string | null | undefined): string {
  if (!filename || filename.trim() === '') {
    return '';
  }

  return filename
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^\x00-\x7F]/g, '')    // Remove non-ASCII
    .replace(/[^a-zA-Z0-9._-]/g, '_'); // Replace unsafe characters
}

function* watchImagesDetails() {
  yield takeEvery(DetailsImagesActionTypes.FETCH_IMAGES, handleFetchImages);
  yield takeEvery(DetailsImagesActionTypes.IMPORT_REVIDEA_IMAGES, handleImportRevideaImages);
  yield takeEvery(DetailsImagesActionTypes.UPLOAD_IMAGES, handleUploadImages);
  yield takeEvery(DetailsImagesActionTypes.DELETE_IMAGES, handleDeleteImages);
  yield takeEvery(DetailsImagesActionTypes.UPDATE_IMAGES, handleUpdateImages);
  yield takeEvery(DetailsImagesActionTypes.UPDATE_IMAGES_SUCCESS, handleUpdateImagesSuccess);
  yield takeEvery(DetailsImagesActionTypes.GET_IMAGE_SRC, handleGetImageSrc);
}

function* detailsImagesSaga() {
  yield all([fork(watchImagesDetails)]);
}

export default detailsImagesSaga;
